import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import { cn } from "~/lib/utils";

interface TabDef {
  key: string;
  label: string;
}
interface TabComponentProps {
  tabs: TabDef[];
  selectedTab: string;
  onTabChange: (tab: string) => void;
  width?: number;
  className?: string;
  hoverClassName?: string;
  containerClassName?: string;
  fullWidth?: boolean;
  queryKey?: string;
}

interface TabProps {
  tab: TabDef;
  currentTab: string;
  setSelectedTab: (tab: string) => void;
  setPosition: (position: { left: number; width: number; opacity: number }) => void;
  isHovered: boolean;
  width?: number;
  className?: string;
  hoverClassName?: string;
  fullWidth?: boolean;
  queryKey?: string;
}

export const TabComponent = ({
  tabs,
  selectedTab,
  onTabChange,
  width,
  className,
  hoverClassName,
  containerClassName,
  fullWidth,
  queryKey,
}: TabComponentProps) => {
  return (
    <div className="w-full">
      <SlideTabs
        tabs={tabs}
        selectedTab={selectedTab}
        setSelectedTab={onTabChange}
        width={width}
        className={className}
        hoverClassName={hoverClassName}
        containerClassName={containerClassName}
        fullWidth={fullWidth}
        queryKey={queryKey}
      />
    </div>
  );
};

const SlideTabs = ({
  tabs,
  selectedTab,
  setSelectedTab,
  width,
  className,
  hoverClassName,
  containerClassName,
  fullWidth,
  queryKey,
}: {
  tabs: TabDef[];
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  width?: number;
  className?: string;
  hoverClassName?: string;
  containerClassName?: string;
  fullWidth?: boolean;
  queryKey?: string;
}) => {
  const [position, setPosition] = useState({
    left: 0,
    width: 0,
    opacity: 0,
  });
  const [isHovered, setIsHovered] = useState(false);

  return (
    <ul
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setPosition((prev) => ({
          ...prev,
          opacity: 0,
        }));
        setIsHovered(false);
      }}
      className={cn("relative mx-auto flex rounded-full border border-textHeadingColor/30 p-1", fullWidth ? "w-full" : "w-fit", containerClassName)}
    >
      {tabs.map((tab) => (
        <Tab
          key={tab.key}
          tab={tab}
          currentTab={selectedTab}
          setSelectedTab={setSelectedTab}
          setPosition={setPosition}
          isHovered={isHovered}
          width={width}
          className={className}
          fullWidth={fullWidth}
          queryKey={queryKey}
        />
      ))}

      <Cursor position={position} hoverClassName={hoverClassName} />
    </ul>
  );
};

const Tab = ({ tab, currentTab, setSelectedTab, setPosition, isHovered, width, className, fullWidth, queryKey }: TabProps) => {
  const ref = useRef<HTMLButtonElement | null>(null);
  const isActive = currentTab === tab.key;
  const { medium } = useFontStyle();
  const { LocalizedLink } = useLocale();
  const { pathname } = useLocation();
  const { lang } = useParams();

  const pathWithoutLang = lang ? pathname.slice(`/${lang}`.length) || "/" : pathname;

  const tabClassName = cn(
    "relative z-10 block cursor-pointer transition-colors duration-200 whitespace-nowrap",
    fullWidth ? "flex-1 text-center" : width ? `w-[${width}px]` : "w-max",
    "px-3 py-2 md:px-5 md:py-3 body_text_m",
    "capitalize tracking-wide rounded-full",
    isActive && !isHovered ? "text-white bg-buttonColor" : "text-textColor hover:text-white",
    medium,
    className,
  );

  if (queryKey) {
    return (
      <Link
        to={`${pathWithoutLang}?${queryKey}=${tab.key}`}
        onClick={() => setSelectedTab(tab.key)}
        onMouseEnter={(e) => {
          const el = e.currentTarget;
          const { width } = el.getBoundingClientRect();
          setPosition({ left: el.offsetLeft, width, opacity: 1 });
        }}
        className={tabClassName}
      >
        {tab.label}
      </Link>
    );
  }

  return (
    <button
      ref={ref}
      onMouseEnter={() => {
        if (!ref?.current) return;
        const { width } = ref.current.getBoundingClientRect();
        setPosition({
          left: ref.current.offsetLeft,
          width,
          opacity: 1,
        });
      }}
      onClick={() => setSelectedTab(tab.key)}
      className={tabClassName}
    >
      {tab.label}
    </button>
  );
};

const Cursor = ({ position, hoverClassName }: { position: { left: number; width: number; opacity: number }; hoverClassName?: string }) => {
  return (
    <motion.li
      animate={{
        ...position,
      }}
      className={cn("absolute h-9 rounded-full bg-buttonColor md:h-[50px]", hoverClassName)}
    />
  );
};

export default TabComponent;
